/**
 * Service pour la gestion des documents du profil prestataire libre
 */
import type { ProfileCompletion } from './profileService';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Obtenir le token d'authentification
 */
const getAuthHeader = (includeContentType = true): HeadersInit => {
  const token = localStorage.getItem('token');
  const headers: HeadersInit = {
    'Authorization': token ? `Bearer ${token}` : '',
    'Accept': 'application/json',
  };
  
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  
  return headers;
};

/**
 * Gestion des erreurs API
 */
const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.errors 
      ? Object.values(errorData.errors).flat().join(', ')
      : errorData.message || `Erreur ${response.status}`;
    throw new Error(errorMessage);
  }
  return response.json();
};

// ============================================
// Types
// ============================================

export interface DocumentMeta {
  id: number;
  type: string;
  type_label: string;
  key: string;
  commentaire: string | null;
  filename: string;
  filesize: number;
  mime_type: string;
  created_at: string;
  updated_at: string;
}

export interface DocumentStatus {
  type: string;
  label: string;
  required: boolean;
  uploaded: boolean;
}

export interface UploadResult {
  document: DocumentMeta;
  message: string;
  completion: ProfileCompletion;
}

// Types de documents disponibles
// CORE-28: Synchronisé avec DocumentEntrepriseMeta::TYPE_LABELS
// ⚠️ cv_equipe supprimé (appartient aux documents accord-cadre)
export const DOCUMENT_TYPES = {
  // Documents requis
  DECLARATION_HONNEUR: 'declaration_honneur',
  KBIS: 'kbis',
  ATTESTATION_FISCALE: 'attestation_fiscale',
  RC_PRO: 'rc_pro',
  // Documents optionnels
  ATTESTATION_SOCIALE: 'attestation_sociale',
  CERTIF_RGE: 'certif_rge',
  LOGO: 'logo',
  RIB: 'rib',
  GARANTIE_DECENNALE: 'garantie_decennale',
  AGREMENTS: 'agrements',
  QUALIFICATIONS: 'qualifications',
} as const;

export type DocumentType = typeof DOCUMENT_TYPES[keyof typeof DOCUMENT_TYPES];

// Labels des types de documents
// CORE-28: Synchronisé avec DocumentEntrepriseMeta::TYPE_LABELS
export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  [DOCUMENT_TYPES.DECLARATION_HONNEUR]: 'Déclaration sur l\'honneur',
  [DOCUMENT_TYPES.KBIS]: 'Extrait Kbis',
  [DOCUMENT_TYPES.ATTESTATION_FISCALE]: 'Attestation fiscale',
  [DOCUMENT_TYPES.RC_PRO]: 'Assurance RC Pro',
  [DOCUMENT_TYPES.ATTESTATION_SOCIALE]: 'Attestation sociale',
  [DOCUMENT_TYPES.CERTIF_RGE]: 'Certification RGE',
  [DOCUMENT_TYPES.LOGO]: 'Logo',
  [DOCUMENT_TYPES.RIB]: 'RIB',
  [DOCUMENT_TYPES.GARANTIE_DECENNALE]: 'Garantie décennale',
  [DOCUMENT_TYPES.AGREMENTS]: 'Agréments',
  [DOCUMENT_TYPES.QUALIFICATIONS]: 'Qualifications',
};

// Documents requis (obligatoires)
// CORE-28: Synchronisé avec DocumentEntrepriseMeta::REQUIRED_TYPES
export const REQUIRED_DOCUMENT_TYPES: DocumentType[] = [
  DOCUMENT_TYPES.DECLARATION_HONNEUR,
  DOCUMENT_TYPES.KBIS,
  DOCUMENT_TYPES.ATTESTATION_FISCALE,
  DOCUMENT_TYPES.RC_PRO,
];

// ============================================
// API Calls
// ============================================

/**
 * Upload un document
 */
export const uploadDocument = async (
  file: File,
  type: DocumentType,
  commentaire?: string
): Promise<UploadResult> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  if (commentaire) {
    formData.append('commentaire', commentaire);
  }

  // Ne pas inclure Content-Type pour FormData (le navigateur le gère)
  const response = await fetch(`${API_URL}/profile/documents`, {
    method: 'POST',
    headers: getAuthHeader(false),
    body: formData,
  });

  const result = await handleResponse(response);
  return result.data;
};

/**
 * Supprime un document
 */
export const deleteDocument = async (documentId: number): Promise<{ message: string; completion: ProfileCompletion }> => {
  const response = await fetch(`${API_URL}/profile/documents/${documentId}`, {
    method: 'DELETE',
    headers: getAuthHeader(),
  });

  const result = await handleResponse(response);
  return result.data;
};

/**
 * Télécharge un document (retourne un Blob)
 */
export const downloadDocument = async (documentKey: string): Promise<Blob> => {
  const token = localStorage.getItem('token');
  
  const response = await fetch(`${API_URL}/profile/documents/${documentKey}/download`, {
    method: 'GET',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
    },
  });

  if (!response.ok) {
    throw new Error(`Erreur lors du téléchargement: ${response.status}`);
  }

  return response.blob();
};

/**
 * Formate la taille d'un fichier en unité lisible
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * CORE-28: Vérifie si un type de document est supporté
 */
export const validateDocumentType = (type: string): { valid: boolean; error?: string } => {
  const validTypes = Object.values(DOCUMENT_TYPES);
  
  if (!validTypes.includes(type as DocumentType)) {
    return {
      valid: false,
      error: `Type de document non supporté: ${type}. Types valides: ${validTypes.join(', ')}`
    };
  }
  
  return { valid: true };
};

/**
 * Vérifie si un fichier est valide pour l'upload
 */
export const validateFile = (file: File, type: DocumentType): { valid: boolean; error?: string } => {
  const isLogo = type === DOCUMENT_TYPES.LOGO;
  const maxSize = isLogo ? 2 * 1024 * 1024 : 10 * 1024 * 1024; // 2MB pour logo, 10MB pour documents
  const allowedTypes = isLogo 
    ? ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    : ['application/pdf'];

  // Vérification de la taille
  if (file.size > maxSize) {
    const maxSizeMb = maxSize / (1024 * 1024);
    return { 
      valid: false, 
      error: `Le fichier ne doit pas dépasser ${maxSizeMb} Mo` 
    };
  }

  // Vérification du type MIME
  if (!allowedTypes.includes(file.type)) {
    const allowedTypesStr = isLogo ? 'JPG, PNG, GIF ou WebP' : 'PDF';
    return { 
      valid: false, 
      error: `Le fichier doit être au format ${allowedTypesStr}` 
    };
  }

  return { valid: true };
};

/**
 * Retourne l'icône appropriée selon le type de document
 */
export const getDocumentIcon = (mimeType: string): string => {
  if (mimeType.startsWith('image/')) {
    return '🖼️';
  }
  if (mimeType === 'application/pdf') {
    return '📄';
  }
  return '📎';
};

export default {
  uploadDocument,
  deleteDocument,
  downloadDocument,
  formatFileSize,
  validateFile,
  validateDocumentType,
  getDocumentIcon,
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_LABELS,
  REQUIRED_DOCUMENT_TYPES,
};
